/**
 * Copy text to the clipboard.
 *
 * Prefers the async Clipboard API, falling back to the legacy `execCommand('copy')`
 * path when it is unavailable — notably in non-secure contexts (plain http, some
 * embedded webviews) where `navigator.clipboard` is `undefined`.
 *
 * Returns `true` on success and `false` on failure; callers own any user-facing
 * notification so each site can keep its own messaging.
 */
export async function copyToClipboard(text: string): Promise<boolean> {
    if (!text) return false;

    if (navigator.clipboard?.writeText) {
        try {
            await navigator.clipboard.writeText(text);
            return true;
        } catch {
            // Secure-context API present but rejected (e.g. permissions/focus);
            // fall through to the legacy path.
        }
    }

    return copyViaExecCommand(text);
}

function copyViaExecCommand(text: string): boolean {
    const textarea = document.createElement("textarea");
    textarea.value = text;
    // Keep it out of view and non-interactive to avoid scroll jumps / mobile zoom.
    textarea.setAttribute("readonly", "");
    textarea.style.position = "fixed";
    textarea.style.top = "-9999px";
    textarea.style.opacity = "0";
    document.body.appendChild(textarea);
    try {
        textarea.select();
        // iOS Safari won't select a readonly field via select() alone; set the range explicitly.
        textarea.setSelectionRange(0, text.length);
        return document.execCommand("copy");
    } catch {
        return false;
    } finally {
        // Always remove the node, even if execCommand throws (e.g. SecurityError in a sandboxed iframe).
        document.body.removeChild(textarea);
    }
}
